import { EnvironmentShape, EnvironmentSettings, EnvironmentTheme, EnvironmentSelection } from './environment.types';

// ─────────────────────────────────────────────────────────────────────────────
// localStorage keys — one bucket per environment slice
// ─────────────────────────────────────────────────────────────────────────────

const STORAGE_KEYS = {
    settings:  'app-settings',
    theme:     'app-theme',
    user:      'app-user',
    selection: 'app-selection'
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Defaults — used when localStorage is empty and /web/config hasn't loaded yet
// ─────────────────────────────────────────────────────────────────────────────

const DEFAULT_SETTINGS: EnvironmentSettings = {
    url:     { api: '', web: '', mobile: '' },
    app:     { code: '', name: '' },
    support: { email: '', mobile: '' }
};

const DEFAULT_THEME: EnvironmentTheme = {
    logo:      '',
    theme:     'default',
    favicon:   '',
    watermark: ''
};

const DEFAULT_SELECTION: EnvironmentSelection = {
    organization: null,
    branch:       null
};

// ─────────────────────────────────────────────────────────────────────────────
// Storage helpers
// ─────────────────────────────────────────────────────────────────────────────

function loadFromStorage<T>(key: string): T | null {
    if (typeof localStorage === 'undefined') return null;
    try {
        const raw = localStorage.getItem(key);
        return raw ? JSON.parse(raw) as T : null;
    } catch {
        return null;
    }
}

function saveToStorage(key: string, value: unknown): void {
    if (typeof localStorage === 'undefined') return;
    try {
        if (value === null || value === undefined) {
            localStorage.removeItem(key);
        } else {
            localStorage.setItem(key, JSON.stringify(value));
        }
    } catch { /* storage quota exceeded — silently ignore */ }
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Creates the environment object.
 *
 * Initial values are read from localStorage (keys prefixed with "app-").
 * Any top-level assignment (e.g. `environment.user = ...`) automatically
 * persists the new value back to localStorage — no manual save calls needed.
 *
 * @example
 *   // After sign-in:
 *   environment.user = { token, superadmin, profile, organizations, access, roles };
 *
 *   // After org/branch selection:
 *   environment.selection = { organization, branch };
 *
 *   // After sign-out:
 *   environment.user      = null;
 *   environment.selection = { organization: null, branch: null };
 */
export function createEnvironment(production: boolean): EnvironmentShape {
    const state: EnvironmentShape = {
        production,
        settings:  loadFromStorage<EnvironmentSettings>(STORAGE_KEYS.settings)  ?? DEFAULT_SETTINGS,
        theme:     loadFromStorage<EnvironmentTheme>(STORAGE_KEYS.theme)         ?? DEFAULT_THEME,
        user:      loadFromStorage(STORAGE_KEYS.user)                            ?? null,
        selection: loadFromStorage<EnvironmentSelection>(STORAGE_KEYS.selection) ?? DEFAULT_SELECTION
    };

    return new Proxy(state, {
        set(target, prop: string, value) {
            (target as any)[prop] = value;
            if (prop in STORAGE_KEYS) {
                saveToStorage(STORAGE_KEYS[prop as keyof typeof STORAGE_KEYS], value);
            }
            return true;
        }
    }) as EnvironmentShape;
}

/**
 * Fetches /web/config and populates environment.settings + environment.theme.
 * Call once in APP_INITIALIZER (or main.ts) before bootstrapping the app.
 * Skips the fetch if settings.url.api is already populated from localStorage.
 *
 * /web/config shape: { url, app, support, layout: { logo, theme, favicon, watermark } }
 */
export async function initEnvironment(env: EnvironmentShape): Promise<void> {
    if (env.settings.url.api) return;   // already loaded from localStorage

    try {
        const res = await fetch('/web/config');
        if (!res.ok) return;
        const config = await res.json();
        if (!config) return;

        env.settings = {
            url:     config.url     ?? DEFAULT_SETTINGS.url,
            app:     config.app     ?? DEFAULT_SETTINGS.app,
            support: config.support ?? DEFAULT_SETTINGS.support
        };

        if (config.layout) {
            env.theme = config.layout as EnvironmentTheme;
        }
    } catch { /* network error — app continues with empty defaults */ }
}